import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "../hooks/useAuth";
import { useInventory } from "../hooks/useInventory";
import useDropdownValues from "../hooks/useDropdownValues";
import OverviewPage from "../pages/OverviewPage";
import InventoryPage from "../pages/InventoryPage";
import ImportPage from "../pages/ImportPage";
import MasterAddressPage from "../pages/MasterAddressPage";
import AnalyticsPage from "../pages/AnalyticsPage";
import Report20Page from "../pages/Report20Page";
import InvoicesPage from "../pages/InvoicesPage";
import BillingAccountsModal from "./modals/BillingAccountsModal";
import Header from "./layout/Header";
import {
  canAccessClientAdministration,
  CLIENT_ADMIN_PAGE,
  isClientAdminPage,
  INVENTORY_ACCOUNT_SEARCH_KEY,
  MASTER_ADDRESS_SEARCH_KEY,
  GLOBAL_SEARCH_INVOICE_HANDOFF_KEY,
} from "../config/constants";
import ActivityLogPage from "../pages/ActivityLogPage";
import {
  getReport20FocusSection,
  isReport20NavPage,
  REPORT20_CLOSED_SITE_FINANCIALS_PAGE,
  // REPORT20_MAIN_PAGE,
  REPORT20_SITE_FOOTPRINT_PAGE,
  REPORT20_SPEND_OVERVIEW_PAGE,
  REPORT20_YTD_BUDGET_PAGE,
  REPORT20_MRC_HISTORY_PAGE,
  REPORT20_DISCONNECTS_PAGE,
  REPORT20_WIRELESS_USAGE_PAGE,
} from "../config/report20Nav";
import {
  clearAppHistoryModal,
  getAppHistoryPage,
  notifyAppNavigate,
  pushAppHistory,
  replaceAppHistory,
} from "../utils/appHistory";

const RegularUserApp = () => {
  const { user, userRole, currentTenant, tenantId, logout, refreshProfile } =
    useAuth();
  const [currentPage, setCurrentPage] = useState("overview");
  const [pageMountKeys, setPageMountKeys] = useState({});
  const [headerOptionsContent, setHeaderOptionsContent] = useState(null);
  const [billingAccountsModalOpen, setBillingAccountsModalOpen] = useState(false);

  // Seed history and keep currentPage in sync with browser Back/Forward.
  useEffect(() => {
    replaceAppHistory({ page: "overview" });
    setCurrentPage("overview");

    const onPopState = (event) => {
      const page = getAppHistoryPage(event.state);
      setCurrentPage(page || "overview");
    };
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);

  useEffect(() => {
    if (isClientAdminPage(currentPage) && !canAccessClientAdministration(userRole)) {
      replaceAppHistory({ page: "overview" });
      setCurrentPage("overview");
    }
  }, [currentPage, userRole]);

  // Options bar content: Master Address and Analytics set their own. Others clear when not inventory/reports/invoices.
  useEffect(() => {
    if (currentPage === "overview") {
      setHeaderOptionsContent(null);
      return;
    }
    if (currentPage === CLIENT_ADMIN_PAGE.ACTIVITY) {
      setHeaderOptionsContent(null);
      return;
    }
    if (currentPage === "master-address") {
      setHeaderOptionsContent(null);
      return;
    }
    if (currentPage === "analytics" || isReport20NavPage(currentPage)) return;
    if (
      currentPage !== "inventory" &&
      !isClientAdminPage(currentPage) &&
      currentPage !== "invoices"
    ) {
      setHeaderOptionsContent(null);
    }
  }, [currentPage]);

  const {
    providers,
    categories,
    statuses,
    serviceTypes,
    billingAccounts,
    billingAccountProviderMap,
    filterCombinations,
    billingAccountsByProvider,
    categoriesByProvider,
    serviceTypesByProvider,
    inventoryFilterProviders,
    inventoryFilterBillingAccounts,
    inventoryFilterBillingAccountProviderMap,
    inventoryFilterBillingAccountsByProvider,
    billingProviders,
    activeBillingAccountsByProvider,
    glCodes,
    loading: dropdownLoading,
    refresh: refreshDropdownValues,
  } = useDropdownValues(true);

  const { inventory } = useInventory();

  const bumpPageMount = useCallback((page) => {
    setPageMountKeys((prev) => ({
      ...prev,
      [page]: (prev[page] || 0) + 1,
    }));
  }, []);

  const navigate = useCallback((page) => {
    if (!page) return;
    // Drop any modal history layer so Back from the next page returns to the
    // page itself (not a stale Edit overlay entry).
    clearAppHistoryModal();
    notifyAppNavigate();
    if (getAppHistoryPage() !== page) {
      pushAppHistory({ page });
    }
    setCurrentPage(page);
  }, []);

  const handleSearchSelect = useCallback(
    (item) => {
      if (!item) return;
      const page = item.page || "inventory";
      const searchValue = String(item.searchValue || item.label || "").trim();

      try {
        if (page === "inventory" && searchValue) {
          sessionStorage.setItem(INVENTORY_ACCOUNT_SEARCH_KEY, searchValue);
        } else if (page === "master-address" && searchValue) {
          sessionStorage.setItem(MASTER_ADDRESS_SEARCH_KEY, searchValue);
        } else if (page === "invoices") {
          sessionStorage.setItem(
            GLOBAL_SEARCH_INVOICE_HANDOFF_KEY,
            JSON.stringify({
              search: searchValue,
              month: item.month || null,
            }),
          );
        }
      } catch {
        /* ignore */
      }

      navigate(page);
      bumpPageMount(page);
    },
    [bumpPageMount, navigate],
  );

  return (
    <div className="min-h-screen bg-brand-background">
      <Header
        tenant={currentTenant}
        user={user}
        userRole={userRole}
        onNavigate={navigate}
        onSearchSelect={handleSearchSelect}
        currentPage={currentPage}
        primaryTextColor={currentTenant?.primaryTextColor || "text-blue-600"}
        onLogout={logout}
        onLogoUpdated={(updatedUser) => refreshProfile()}
        isSuperAdmin={false}
        showOptionsBar={
          (isClientAdminPage(currentPage) &&
            currentPage !== CLIENT_ADMIN_PAGE.ACTIVITY) ||
          currentPage === "analytics" ||
          isReport20NavPage(currentPage) ||
          currentPage === "invoices"
        }
        optionsContent={headerOptionsContent}
      />
      <main className="pt-0 min-h-screen">
        {currentPage === "overview" && (
          <OverviewPage
            tenant={currentTenant}
            tenantId={tenantId}
            userRole={userRole}
            onNavigate={navigate}
          />
        )}
        {currentPage === "inventory" && (
          <InventoryPage
            key={`inventory-${pageMountKeys.inventory || 0}`}
            userRole={userRole}
            tenant={currentTenant}
            tenantId={tenantId}
            providers={providers}
            categories={categories}
            serviceTypes={serviceTypes}
            billingAccounts={billingAccounts}
            billingAccountProviderMap={billingAccountProviderMap}
            filterCombinations={filterCombinations}
            billingAccountsByProvider={billingAccountsByProvider}
            categoriesByProvider={categoriesByProvider}
            serviceTypesByProvider={serviceTypesByProvider}
            filterBarProviders={inventoryFilterProviders}
            filterBarBillingAccounts={inventoryFilterBillingAccounts}
            filterBarBillingAccountProviderMap={inventoryFilterBillingAccountProviderMap}
            filterBarBillingAccountsByProvider={inventoryFilterBillingAccountsByProvider}
            dropdownLoading={dropdownLoading}
            refreshDropdownValues={refreshDropdownValues}
            setHeaderOptionsContent={setHeaderOptionsContent}
            onNavigate={navigate}
          />
        )}
        {currentPage === CLIENT_ADMIN_PAGE.ACTIVITY &&
          canAccessClientAdministration(userRole) && <ActivityLogPage />}
        {isClientAdminPage(currentPage) &&
          currentPage !== CLIENT_ADMIN_PAGE.ACTIVITY &&
          canAccessClientAdministration(userRole) && (
          <ImportPage
            clientAdminRoute={currentPage}
            onNavigate={navigate}
            tenant={currentTenant}
            tenantId={tenantId}
            inventory={inventory}
            providers={billingProviders}
            categories={categories}
            serviceTypes={serviceTypes}
            billingAccountsByProvider={activeBillingAccountsByProvider}
            glCodes={glCodes}
            dropdownLoading={dropdownLoading}
            refreshDropdownValues={refreshDropdownValues}
            setHeaderOptionsContent={setHeaderOptionsContent}
            userRole={userRole}
          />
        )}
        {currentPage === "analytics" && (
          <AnalyticsPage
            tenant={currentTenant}
            tenantId={tenantId}
            userRole={userRole}
            setHeaderOptionsContent={setHeaderOptionsContent}
            providers={providers}
            categories={categories}
            statuses={statuses}
          />
        )}
        {isReport20NavPage(currentPage) && (
          <Report20Page
            tenantId={tenantId}
            setHeaderOptionsContent={setHeaderOptionsContent}
            focusSection={getReport20FocusSection(currentPage)}
            ytdBudgetOnly={currentPage === REPORT20_YTD_BUDGET_PAGE}
            footprintOnly={currentPage === REPORT20_SITE_FOOTPRINT_PAGE}
            closedSitesFinancialsOnly={currentPage === REPORT20_CLOSED_SITE_FINANCIALS_PAGE}
            spendOverviewOnly={currentPage === REPORT20_SPEND_OVERVIEW_PAGE}
            mrcHistoryOnly={currentPage === REPORT20_MRC_HISTORY_PAGE}
            disconnectsOnly={currentPage === REPORT20_DISCONNECTS_PAGE}
            wirelessUsageOnly={currentPage === REPORT20_WIRELESS_USAGE_PAGE}
            // showReportBudgetCard={currentPage === REPORT20_MAIN_PAGE}
            showReportBudgetCard={false}
            glCodes={glCodes}
          />
        )}
        {currentPage === "master-address" && (
          <MasterAddressPage
            key={`master-address-${pageMountKeys["master-address"] || 0}`}
            userRole={userRole}
            tenant={currentTenant}
            tenantId={tenantId}
            setHeaderOptionsContent={setHeaderOptionsContent}
            onNavigate={navigate}
          />
        )}
        {currentPage === "invoices" && (
          <InvoicesPage
            key={`invoices-${pageMountKeys.invoices || 0}`}
            userRole={userRole}
            tenant={currentTenant}
            tenantId={tenantId}
            setHeaderOptionsContent={setHeaderOptionsContent}
            onNavigate={navigate}
            refreshDropdownValues={refreshDropdownValues}
            glCodes={glCodes}
            onAddProviderBillingAccount={
              userRole === "Admin"
                ? () => setBillingAccountsModalOpen(true)
                : undefined
            }
          />
        )}
      </main>
      {userRole === "Admin" && (
        <BillingAccountsModal
          isOpen={billingAccountsModalOpen}
          onClose={() => setBillingAccountsModalOpen(false)}
          refreshDropdownValues={refreshDropdownValues}
        />
      )}
    </div>
  );
};

export default RegularUserApp;
